import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { GripVertical, Plus, Trash2, Image, Type, FileText, Link as LinkIcon, ExternalLink, Save, Eye, ArrowLeft, Monitor, Smartphone, RatioIcon } from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { apiFetch, apiUpload, API } from '../../utils/api';

const CATEGORIES = [
  { label: 'Fashion', value: 'fashion' },
  { label: 'Lifestyle', value: 'lifestyle' },
  { label: 'Home Decor', value: 'home-decor' },
  { label: 'Other', value: 'other' },
];

const BLOCK_TYPES = [
  { type: 'title', label: 'Title', icon: Type },
  { type: 'text', label: 'Text', icon: FileText },
  { type: 'image', label: 'Image + Text', icon: Image },
  { type: 'affiliate', label: 'Product + Link', icon: LinkIcon },
];

const ASPECT_RATIOS = [
  { label: '4:3', value: '4/3' },
  { label: '1:1', value: '1/1' },
  { label: '16:9', value: '16/9' },
  { label: '3:4', value: '3/4' },
  { label: '9:16', value: '9/16' },
  { label: 'Auto', value: 'auto' },
];

function generateId() {
  return Math.random().toString(36).substring(2, 15);
}

export default function ArticleBuilder() {
  const navigate = useNavigate();
  const { articleId } = useParams();
  const isEdit = !!articleId;

  const [title, setTitle] = useState('');
  const [category, setCategory] = useState('fashion');
  const [coverImage, setCoverImage] = useState('');
  const [blocks, setBlocks] = useState([]);
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [uploading, setUploading] = useState({});
  const [previewDevice, setPreviewDevice] = useState('desktop');

  useEffect(() => {
    if (isEdit) {
      apiFetch('/articles/all')
        .then(r => r.json())
        .then(articles => {
          const article = articles.find(a => a.id === articleId);
          if (article) {
            setTitle(article.title);
            setCategory(article.category);
            setCoverImage(article.cover_image || '');
            setBlocks(article.blocks || []);
          }
        })
        .catch(() => toast.error('Failed to load article'));
    }
  }, [articleId, isEdit]);

  const addBlock = (type) => {
    const newBlock = {
      block_id: generateId(),
      block_type: type,
      content: '',
      image_url: null,
      affiliate_url: null,
      aspect_ratio: '4/3',
      position: blocks.length,
    };
    setBlocks([...blocks, newBlock]);
  };

  const updateBlock = (blockId, field, value) => {
    setBlocks(blocks.map(b => b.block_id === blockId ? { ...b, [field]: value } : b));
  };

  const removeBlock = (blockId) => {
    setBlocks(blocks.filter(b => b.block_id !== blockId));
  };

  const onDragEnd = (result) => {
    if (!result.destination) return;
    const items = Array.from(blocks);
    const [reordered] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reordered);
    setBlocks(items.map((b, i) => ({ ...b, position: i })));
  };

  const handleImageUpload = async (blockId, file, isCover = false) => {
    const key = isCover ? 'cover' : blockId;
    setUploading(u => ({ ...u, [key]: true }));
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await apiUpload('/upload', formData);
      if (!res.ok) throw new Error('Upload failed');
      const data = await res.json();
      const imageUrl = `${API}/files/${data.path}`;
      if (isCover) {
        setCoverImage(imageUrl);
      } else {
        updateBlock(blockId, 'image_url', imageUrl);
      }
      toast.success('Image uploaded');
    } catch {
      toast.error('Upload failed');
    }
    setUploading(u => ({ ...u, [key]: false }));
  };

  const handleSave = async (status = 'draft') => {
    if (!title.trim()) {
      toast.error('Title is required');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title,
        category,
        cover_image: coverImage || null,
        blocks: blocks.map((b, i) => ({ ...b, position: i })),
        status,
      };
      const url = isEdit ? `/articles/${articleId}` : `/articles`;
      const method = isEdit ? 'PUT' : 'POST';
      const res = await apiFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error('Save failed');
      toast.success(status === 'published' ? 'Article published!' : 'Article saved!');
      navigate('/admin');
    } catch {
      toast.error('Failed to save article');
    }
    setSaving(false);
  };

  const renderBlockEditor = (block, index) => (
    <Draggable key={block.block_id} draggableId={block.block_id} index={index}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          className={`border border-[#E5E7EB] bg-white mb-3 ${snapshot.isDragging ? 'shadow-lg opacity-80' : ''}`}
          data-testid={`block-editor-${index}`}
        >
          {/* Block Header */}
          <div className="flex items-center gap-2 px-4 py-2 bg-[#F4F4F4] border-b border-[#E5E7EB]">
            <div {...provided.dragHandleProps} className="cursor-grab active:cursor-grabbing" data-testid={`drag-handle-${index}`}>
              <GripVertical className="w-4 h-4 text-[#6B7280]" />
            </div>
            <span className="text-xs uppercase tracking-widest text-[#6B7280] font-medium flex-1">
              {BLOCK_TYPES.find(bt => bt.type === block.block_type)?.label || block.block_type}
            </span>
            <button onClick={() => removeBlock(block.block_id)} className="text-red-400 hover:text-red-600 transition-colors" data-testid={`remove-block-${index}`}>
              <Trash2 className="w-4 h-4" />
            </button>
          </div>

          {/* Block Content */}
          <div className="p-4 space-y-3">
            {/* Title block */}
            {block.block_type === 'title' && (
              <Input
                placeholder="Section Title"
                value={block.content}
                onChange={e => updateBlock(block.block_id, 'content', e.target.value)}
                className="border-[#E5E7EB] rounded-none text-lg font-['Playfair_Display'] font-semibold"
                data-testid={`block-title-input-${index}`}
              />
            )}

            {/* Text block */}
            {block.block_type === 'text' && (
              <textarea
                placeholder="Enter your text content..."
                value={block.content}
                onChange={e => updateBlock(block.block_id, 'content', e.target.value)}
                rows={4}
                className="w-full border border-[#E5E7EB] p-3 text-sm focus:outline-none focus:border-[#0A1128] resize-y"
                data-testid={`block-text-input-${index}`}
              />
            )}

            {/* Image block */}
            {(block.block_type === 'image' || block.block_type === 'affiliate') && (
              <>
                <div className="flex gap-3 items-center">
                  <label className="flex-1">
                    <div className={`border border-dashed border-[#E5E7EB] p-4 text-center cursor-pointer hover:border-[#0A1128] transition-colors ${uploading[block.block_id] ? 'opacity-50' : ''}`}>
                      {block.image_url ? (
                        <img src={block.image_url} alt="" className="w-full max-h-48 object-cover" style={{ aspectRatio: (block.aspect_ratio && block.aspect_ratio !== 'auto') ? block.aspect_ratio : undefined }} />
                      ) : (
                        <div className="text-[#6B7280] text-xs uppercase tracking-widest">
                          {uploading[block.block_id] ? 'Uploading...' : 'Click to upload image'}
                        </div>
                      )}
                    </div>
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={e => e.target.files?.[0] && handleImageUpload(block.block_id, e.target.files[0])}
                      data-testid={`block-image-upload-${index}`}
                    />
                  </label>
                </div>
                <Input
                  placeholder="Or paste image URL"
                  value={block.image_url || ''}
                  onChange={e => updateBlock(block.block_id, 'image_url', e.target.value)}
                  className="border-[#E5E7EB] rounded-none text-sm"
                  data-testid={`block-image-url-${index}`}
                />
                {/* Aspect Ratio Selector */}
                <div className="flex items-center gap-2">
                  <RatioIcon className="w-4 h-4 text-[#6B7280]" />
                  <span className="text-xs uppercase tracking-widest text-[#6B7280]">Ratio:</span>
                  <div className="flex gap-1">
                    {ASPECT_RATIOS.map(ar => (
                      <button
                        key={ar.value}
                        type="button"
                        onClick={() => updateBlock(block.block_id, 'aspect_ratio', ar.value)}
                        className={`px-2 py-1 text-xs border transition-colors ${
                          (block.aspect_ratio || '4/3') === ar.value
                            ? 'bg-[#0A1128] text-white border-[#0A1128]'
                            : 'border-[#E5E7EB] text-[#6B7280] hover:border-[#0A1128] hover:text-[#0A1128]'
                        }`}
                        data-testid={`aspect-ratio-${ar.value.replace('/', '-')}-${index}`}
                      >
                        {ar.label}
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}

            {/* Description for image/affiliate */}
            {(block.block_type === 'image' || block.block_type === 'affiliate') && (
              <textarea
                placeholder="Description / Information about this product..."
                value={block.content}
                onChange={e => updateBlock(block.block_id, 'content', e.target.value)}
                rows={3}
                className="w-full border border-[#E5E7EB] p-3 text-sm focus:outline-none focus:border-[#0A1128] resize-y"
                data-testid={`block-desc-input-${index}`}
              />
            )}

            {/* Affiliate URL */}
            {block.block_type === 'affiliate' && (
              <div className="flex items-center gap-2">
                <LinkIcon className="w-4 h-4 text-[#6B7280]" />
                <Input
                  placeholder="Affiliate Link URL"
                  value={block.affiliate_url || ''}
                  onChange={e => updateBlock(block.block_id, 'affiliate_url', e.target.value)}
                  className="border-[#E5E7EB] rounded-none text-sm flex-1"
                  data-testid={`block-affiliate-url-${index}`}
                />
              </div>
            )}
          </div>
        </div>
      )}
    </Draggable>
  );

  // Preview component
  const renderPreviewBlock = (block, index) => {
    const isEven = index % 2 === 0;
    switch (block.block_type) {
      case 'title':
        return (
          <div key={block.block_id} className="mb-8">
            <h2 className="font-['Playfair_Display'] text-xl font-bold text-[#0A1128]">{block.content || 'Untitled Section'}</h2>
          </div>
        );
      case 'text':
        return (
          <div key={block.block_id} className="mb-8">
            <p className="text-[#6B7280] text-sm leading-relaxed whitespace-pre-line">{block.content || 'Text content...'}</p>
          </div>
        );
      case 'image':
        return (
          <div key={block.block_id} className="mb-8">
            <div className={`grid ${block.content && previewDevice === 'desktop' ? 'grid-cols-2' : 'grid-cols-1'} gap-4 items-center`}>
              <div className={!isEven && block.content && previewDevice === 'desktop' ? 'order-2' : ''}>
                {block.image_url ? (
                  <img src={block.image_url} alt="" className="w-full object-cover" style={{ aspectRatio: (block.aspect_ratio && block.aspect_ratio !== 'auto') ? block.aspect_ratio : '4/3' }} />
                ) : (
                  <div className="w-full bg-[#F4F4F4] flex items-center justify-center text-[#6B7280] text-xs" style={{ aspectRatio: (block.aspect_ratio && block.aspect_ratio !== 'auto') ? block.aspect_ratio : '4/3' }}>No Image</div>
                )}
              </div>
              {block.content && (
                <div className={!isEven && previewDevice === 'desktop' ? 'order-1' : ''}>
                  <p className="text-[#6B7280] text-sm leading-relaxed">{block.content}</p>
                </div>
              )}
            </div>
          </div>
        );
      case 'affiliate':
        return (
          <div key={block.block_id} className="mb-8 border border-[#E5E7EB] p-6">
            <div className={`grid ${block.image_url && previewDevice === 'desktop' ? 'grid-cols-2' : 'grid-cols-1'} gap-4 items-center`}>
              {block.image_url && (
                <div className={!isEven && previewDevice === 'desktop' ? 'order-2' : ''}>
                  <img src={block.image_url} alt="" className="w-full object-cover" style={{ aspectRatio: (block.aspect_ratio && block.aspect_ratio !== 'auto') ? block.aspect_ratio : '4/3' }} />
                </div>
              )}
              <div className={!isEven && block.image_url && previewDevice === 'desktop' ? 'order-1' : ''}>
                {block.content && <p className="text-[#0A1128] text-sm leading-relaxed mb-4">{block.content}</p>}
                {block.affiliate_url && (
                  <div className="bg-[#0A1128] text-white px-4 py-2 text-xs uppercase tracking-widest inline-flex items-center gap-2">
                    Shop the Look <ExternalLink className="w-3 h-3" />
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div data-testid="article-builder">
      {/* Top Bar */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate('/admin')} className="text-[#6B7280] hover:text-[#0A1128]" data-testid="back-to-admin">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="font-['Playfair_Display'] text-2xl font-bold text-[#0A1128]">
            {isEdit ? 'Edit Article' : 'New Article'}
          </h1>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => setShowPreview(!showPreview)}
            className="rounded-none border-[#0A1128] text-[#0A1128] uppercase tracking-widest text-xs"
            data-testid="toggle-preview-btn"
          >
            <Eye className="w-4 h-4 mr-2" /> {showPreview ? 'Editor' : 'Preview'}
          </Button>
          <Button
            onClick={() => handleSave('draft')}
            disabled={saving}
            className="rounded-none bg-[#6B7280] text-white uppercase tracking-widest text-xs hover:bg-[#6B7280]/90"
            data-testid="save-draft-btn"
          >
            <Save className="w-4 h-4 mr-2" /> Save Draft
          </Button>
          <Button
            onClick={() => handleSave('published')}
            disabled={saving}
            className="rounded-none bg-[#0A1128] text-white uppercase tracking-widest text-xs hover:bg-[#0A1128]/90"
            data-testid="publish-btn"
          >
            Publish
          </Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-8">
        {/* Editor Panel */}
        <div className={showPreview ? 'hidden lg:block' : ''}>
          {/* Article Meta */}
          <div className="border border-[#E5E7EB] p-4 mb-4 space-y-3" data-testid="article-meta">
            <Input
              placeholder="Article Title"
              value={title}
              onChange={e => setTitle(e.target.value)}
              className="border-0 border-b border-[#E5E7EB] rounded-none text-xl font-['Playfair_Display'] font-bold focus-visible:ring-0 focus-visible:border-[#0A1128] px-0"
              data-testid="article-title-input"
            />
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="rounded-none border-[#E5E7EB] w-48" data-testid="category-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CATEGORIES.map(c => (
                  <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Cover Image */}
            <div>
              <label className="text-xs uppercase tracking-widest text-[#6B7280] mb-2 block">Cover Image</label>
              <div className="flex gap-2">
                <label className="flex-1">
                  <div className={`border border-dashed border-[#E5E7EB] p-3 text-center cursor-pointer hover:border-[#0A1128] transition-colors text-xs text-[#6B7280] ${uploading.cover ? 'opacity-50' : ''}`}>
                    {coverImage ? (
                      <img src={coverImage} alt="Cover" className="w-full max-h-24 object-cover" />
                    ) : (
                      uploading.cover ? 'Uploading...' : 'Upload cover image'
                    )}
                  </div>
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={e => e.target.files?.[0] && handleImageUpload(null, e.target.files[0], true)}
                    data-testid="cover-image-upload"
                  />
                </label>
                <Input
                  placeholder="Or paste URL"
                  value={coverImage}
                  onChange={e => setCoverImage(e.target.value)}
                  className="border-[#E5E7EB] rounded-none text-sm flex-1"
                  data-testid="cover-image-url"
                />
              </div>
            </div>
          </div>

          {/* Blocks */}
          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId="blocks">
              {(provided) => (
                <div ref={provided.innerRef} {...provided.droppableProps} data-testid="blocks-container">
                  {blocks.map((block, index) => renderBlockEditor(block, index))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>

          {/* Add Block Buttons */}
          <div className="flex flex-wrap gap-2 mt-4" data-testid="add-block-buttons">
            {BLOCK_TYPES.map(bt => (
              <Button
                key={bt.type}
                variant="outline"
                onClick={() => addBlock(bt.type)}
                className="rounded-none border-[#E5E7EB] text-[#0A1128] text-xs uppercase tracking-widest hover:bg-[#0A1128] hover:text-white"
                data-testid={`add-${bt.type}-block-btn`}
              >
                <Plus className="w-3 h-3 mr-1" /> {bt.label}
              </Button>
            ))}
          </div>
        </div>

        {/* Preview Panel */}
        <div className={`border border-[#E5E7EB] bg-white ${!showPreview ? 'hidden lg:block' : ''}`} data-testid="preview-panel">
          <div className="px-4 py-2 bg-[#F4F4F4] border-b border-[#E5E7EB] flex items-center justify-between">
            <span className="text-xs uppercase tracking-widest text-[#6B7280] font-medium">Live Preview</span>
            <div className="flex items-center gap-1" data-testid="device-toggle">
              <button
                onClick={() => setPreviewDevice('desktop')}
                className={`p-1.5 rounded transition-colors ${previewDevice === 'desktop' ? 'bg-[#0A1128] text-white' : 'text-[#6B7280] hover:text-[#0A1128]'}`}
                title="Desktop view"
                data-testid="preview-desktop-btn"
              >
                <Monitor className="w-4 h-4" />
              </button>
              <button
                onClick={() => setPreviewDevice('mobile')}
                className={`p-1.5 rounded transition-colors ${previewDevice === 'mobile' ? 'bg-[#0A1128] text-white' : 'text-[#6B7280] hover:text-[#0A1128]'}`}
                title="Mobile view"
                data-testid="preview-mobile-btn"
              >
                <Smartphone className="w-4 h-4" />
              </button>
            </div>
          </div>
          <div className={`max-h-[calc(100vh-200px)] overflow-y-auto ${previewDevice === 'mobile' ? 'flex justify-center bg-[#F4F4F4]' : ''}`}>
            <div className={`p-6 ${previewDevice === 'mobile' ? 'w-[375px] bg-white min-h-[600px] border-x border-[#E5E7EB] shadow-sm' : ''}`} data-testid="preview-content">
            {coverImage && (
              <div className={`mb-8 ${previewDevice === 'desktop' ? '-mx-6 -mt-6' : '-mx-6 -mt-6'}`}>
                <div className={`relative ${previewDevice === 'mobile' ? 'h-36' : 'h-48'}`}>
                  <img src={coverImage} alt="Cover" className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  <div className="absolute bottom-4 left-6">
                    <p className="overline text-white/50 mb-1">{category.replace('-', ' ')}</p>
                    <h1 className="font-['Playfair_Display'] text-2xl font-bold text-white">{title || 'Article Title'}</h1>
                  </div>
                </div>
              </div>
            )}
            {!coverImage && (
              <div className="mb-8">
                <p className="overline text-[#6B7280] mb-2">{category.replace('-', ' ')}</p>
                <h1 className="font-['Playfair_Display'] text-2xl font-bold text-[#0A1128]">{title || 'Article Title'}</h1>
              </div>
            )}
            {blocks.map((block, i) => renderPreviewBlock(block, i))}
            {blocks.length === 0 && (
              <p className="text-[#6B7280] text-sm text-center py-12">Add blocks to see the preview</p>
            )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
